import React, { useEffect, useState } from "react";
import {
  Box,
  Switch,
  Typography,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import { useSelector } from "react-redux";
import { enableDMApi, disableDMApi, getDMStatusApi } from "../../api/messagingApi";

const DMToggle = ({ userId, userName }) => {
  const currentAdmin = useSelector((state) => state.adminauth.admin);

  // All hooks must be called unconditionally before any early return
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const isSuperAdmin = !!currentAdmin?.adminUserId;
  const isCoachAdmin = !currentAdmin?.adminUserId && currentAdmin?.userType === "Coach";
  const canUseDM = isSuperAdmin || isCoachAdmin;

  useEffect(() => {
    if (!userId || !canUseDM) return;
    setLoading(true);
    getDMStatusApi(userId)
      .then((data) => setEnabled(data.isEnabled))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, canUseDM]);

  // Conditional render after all hooks
  if (!canUseDM) return null;

  const handleToggle = async () => {
    setToggling(true);
    try {
      if (enabled) {
        await disableDMApi(userId);
        setEnabled(false);
        setSnack({ open: true, message: `DM disabled for ${userName || "user"}`, severity: "info" });
      } else {
        const res = await enableDMApi(userId);
        setEnabled(true);
        setSnack({
          open: true,
          message: `DM enabled for ${userName || "user"}.`,
          severity: "success",
        });
        // Navigate to Messaging tab and open this conversation
        window.dispatchEvent(new CustomEvent("admin:openMessaging", {
          detail: { conversationId: res?.conversation?._id },
        }));
      }
    } catch {
      setSnack({ open: true, message: "Failed to update DM status", severity: "error" });
    } finally {
      setToggling(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1.5,
          border: "1px solid",
          borderColor: enabled ? "#BFDBFE" : "#E5E7EB",
          borderRadius: 2,
          bgcolor: enabled ? "#EFF6FF" : "#F9FAFB",
          transition: "all 0.2s",
          width: "100%",
        }}
      >
        <ChatIcon sx={{ color: enabled ? "#368AD0" : "#9CA3AF", fontSize: 20, flexShrink: 0 }} />
        <Box flex={1}>
          <Typography fontSize={13} fontWeight={600} color={enabled ? "#368AD0" : "text.primary"}>
            Direct Messaging
          </Typography>
        </Box>
        {loading ? (
          <CircularProgress size={18} sx={{ color: "#368AD0" }} />
        ) : (
          <Tooltip title={enabled ? "Disable DM" : "Enable DM"}>
            <Switch
              checked={enabled}
              onChange={handleToggle}
              disabled={toggling}
              size="small"
              color="primary"
            />
          </Tooltip>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DMToggle;
